:host,
:root {
  /* v2.0 colours */
  --uui-signature-red: #eb0017;
  --uui-signature-red-hover: #b80012;
  --uui-navy: #262836;
  --uui-teal-dark: #007585;
  --uui-teal-dark-hover: #003c55;
  --uui-teal-dark-active: #003c55;
  --uui-teal-light: #29b0c3;
  --uui-error: #ea2238;
  --uui-error-dark: #ca110f;
  --uui-error-background: #fcdcdc;
  --uui-warning: #ffa600;
  --uui-warning-dark: #996300;
  --uui-warning-background: #fff6b7;
  --uui-success: #12a88a;
  --uui-success-dark: #0d725e;
  --uui-success-background: #c1f2e9;
  --uui-information: #0055a8;
  --uui-information-dark: #003b75;
  --uui-information-background: #cceaf5;
  --uui-gray-01: #46535e;
  --uui-gray-02: #5d6d78;
  --uui-gray-03: #82939a;
  --uui-gray-04: #acc0c4;
  --uui-gray-05: #cddbde;
  --uui-gray-06: #e5eff0;
  --uui-gray-07: #eef6f7;
  --uui-gray-08: #f9fcfc;
  /* v2.0 inverse colours */
  --uui-white: #ffffff;
  --uui-error-inverse: #f05f6f;
  --uui-warning-inverse: var(--uui-warning);
  --uui-success-inverse: #15b797;

  // peacock is deprecated as of May 2023
  // mapping to teal dark
  // keeping peacock variables for backwards compatibility
  --uui-peacock: var(--uui-teal-dark);
  --uui-peacock-active: var(--uui-teal-dark-active);
  --uui-peacock-hover: var(--uui-teal-dark-hover);

  /* Data Visualization */
  --uui-color-dataviz-mint: #8cf2de;
  --uui-color-dataviz-mint-hover: #5eedd1;
  --uui-color-dataviz-mint-active: #30e8c4;
  --uui-color-dataviz-aqua: #38cbd2;
  --uui-color-dataviz-aqua-hover: #28a9af;
  --uui-color-dataviz-aqua-active: #1e8186;
  --uui-color-dataviz-lagoon: #00a3c7;
  --uui-color-dataviz-lagoon-hover: #007994;
  --uui-color-dataviz-lagoon-active: #004f61;
  --uui-color-dataviz-marine: #007bb6;
  --uui-color-dataviz-marine-hover: #005983;
  --uui-color-dataviz-marine-active: #003650;
  --uui-color-dataviz-cobalt: #00519b;
  --uui-color-dataviz-cobalt-hover: #003668;
  --uui-color-dataviz-cobalt-active: #001c35;
  --uui-color-dataviz-blue: #012774;
  --uui-color-dataviz-blue-hover: #0138a7;
  --uui-color-dataviz-blue-active: #001d5f;
  --uui-color-dataviz-navy: #001b5a;
  --uui-color-dataviz-midnight: #011641;
  --uui-color-dataviz-plum: #65257e;
  --uui-color-dataviz-plum-hover: #260e2f;
  --uui-color-dataviz-plum-active: #001d5f;
  --uui-color-dataviz-magenta: #a21f79;
  --uui-color-dataviz-magenta-hover: #771759;
  --uui-color-dataviz-magenta-active: #4c0f39;
  --uui-color-dataviz-raspberry: #d22a67;
  --uui-color-dataviz-raspberry-hover: #a72252;
  --uui-color-dataviz-raspberry-active: #7d193d;
  --uui-color-dataviz-red: #f24f4d;
  --uui-color-dataviz-red-hover: #ef201d;
  --uui-color-dataviz-red-active: #ca110f;
  --uui-color-dataviz-orange: #ff7d2e;
  --uui-color-dataviz-orange-hover: #fa5e00;
  --uui-color-dataviz-orange-active: #e05500;
  --uui-color-dataviz-yellow: #ffa600;
  --uui-color-dataviz-yellow-hover: #cc8500;
  --uui-color-dataviz-yellow-active: #996400;
  --uui-color-dataviz-lime: #8abd45;
  --uui-color-dataviz-green: #15b797;
  --uui-color-dataviz-green-hover: #108971;
  --uui-color-dataviz-green-active: #0b5b4b;
  --uui-color-dataviz-teal: #29b0c3;
  --uui-color-dataviz-pink: #d10058;
  --uui-color-dataviz-pink-hover: #9e0043;
  --uui-color-dataviz-pink-active: #6b002d;

  /* opacity */
  --uui-disabled-opacity: 0.5;
  --uui-opacity-darker: 0.87;
  --uui-opacity-lighter: 0.6;

  /* radius */
  --uui-border-radius-sm: 0.125rem;
  --uui-border-radius: 0.25rem;
  --uui-border-radius-md: 0.5rem;
  --uui-border-radius-lg: 0.75rem;

  /* borders */
  --uui-border-base: 1px solid var(--uui-gray-04, #acc0c4);

  /* shadows */
  --uui-shadow-base: 0px 2px 24px rgba(192, 192, 192, 0.5);

  /* font weight sizes */
  --uui-font-weight-light: 300;
  --uui-font-weight-normal: 400;
  --uui-font-weight-bold: 700;

  /* font sizes */
  --uui-default-font-size: 1rem; // 16px
  --uui-sm-line-height: 1.25rem; // 20px
  --uui-default-line-height: 1.5rem; // 24px
  --uui-extra-small-font-size: 0.75rem; // 12px
  --uui-extra-small-line-height: 1rem; // 16px
  --uui-small-font-size: 0.875rem; // 14px
  --uui-small-line-height: 1.125rem; // 18px
  --uui-large-font-size: 1.125rem; // 18px
  --uui-large-line-height: 1.75rem; // 28px
  --uui-extra-large-font-size: 1.3125rem; // 21px
  --uui-extra-large-line-height: 2rem; // 32px

  --uui-dv-large-font-size: 2.5rem; // 40px
  --uui-dv-large-line-height: 2.75rem; // 44px
  --uui-dv-medium-font-size: 1.875rem; // 30px
  --uui-dv-extra-small-line-height: 0.75rem; // 12px

  /* margins */
  --uui-small-bottom-margin: 0.5rem; // 8px
  --uui-medium-bottom-margin: 0.75rem; // 12px
  --uui-large-bottom-margin: 1rem; // ~16px;
  --uui-extra-large-bottom-margin: 1.25rem; // ~20px;

  /* paddings */
  --uui-xx-small-padding: 0.125rem; // 2px
  --uui-x-small-padding: 0.25rem; // 4px
  --uui-small-padding: 0.5rem; // 8px
  --uui-medium-padding: 0.75rem; // 12px
  --uui-large-padding: 1rem; // ~16px;
  --uui-x-large-padding: 1.25rem; // ~20px;
  --uui-xx-large-padding: 2rem; // ~32px;

  /* gap */
  --uui-small-gap: 0.25rem; // 4px
  --uui-medium-gap: 0.5rem; // 8px
  --uui-large-gap: 0.75rem; // 12px;

  /* spacing */
  --uui-extra-small-spacing: 0.75rem; // 12px
  --uui-small-spacing: 1.25rem; // 20px
  --uui-medium-spacing: 2rem; // 32px
  --uui-large-spacing: 2.5rem; // 40px;

  /* page widths */
  --uui-page-width-large: 1240px;
  --uui-page-width-medium: 1200px;

  /* Transition */
  --uui-short-transition: all 0.2s;
  --uui-base-transition: all 0.3s;
  --uui-long-transition: all 0.5s;

  /* Shadow */
  --uui-container-shadow: 0px 0px 6px rgba(192, 192, 192, 0.5);

  /* Backgrounds */
  --uui-gray-05-background: rgba(205, 219, 222, 0.5);
  --uui-gray-04-background: rgba(172, 192, 196, 0.5);

  /* Input Control */
  --uui-input-padding: 0.6875em 0.75em;
  --uui-input-padding-hover: 0.625em 0.6875em;
  --uui-input-radius: 0.25em;
  --uui-input-color: var(--uui-theme-secondary-400);
  --uui-input-border-color: var(--uui-theme-secondary-400);
  --uui-input-border-color-hover: var(--uui-theme-core-400);
  --uui-input-border-color-active: var(--uui-theme-core-600);
  --uui-input-border-color-error: var(--uui-theme-error-600);
  --uui-input-border-color-warning: var(--uui-theme-warning-600);

  /* Divider */
  --uui-divider-color: var(--uui-gray-04);
}
